import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Form, InputGroup, Modal } from "react-bootstrap";
import logger from "../../utils/logging";
import ApiService from "../../services/ApiService";
import ToastSuccess from "../ToastSuccess";
import { isApiError } from "../../errors";
import { ERROR_CODE_PDF_CORRUPTED_FORM_FIELDS, ERROR_CODE_PDF_NO_FORM_FIELDS } from "../../errors/backendErrorCodes";

interface UploadError {
    title: string;
    message: string;
}

interface UploadModalProps {
    show: boolean;
    onHide: () => void;
    apiService: ApiService;
    onSuccess: () => void;
    maxFileSizeBytes?: number;
}

const UploadModal: React.FC<UploadModalProps> = ({ show, onHide, apiService, onSuccess, maxFileSizeBytes }) => {
    const { t } = useTranslation("templates");
    const [submitError, setSubmitError] = useState<UploadError | null>(null);
    const [validated, setValidated] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>("");
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [successToastMessage, setSuccessToastMessage] = useState<string | null>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
        setSubmitError(null);
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);

        // Let Bootstrap validation show field-level errors
        if (!form.checkValidity()) {
            return;
        }

        // Additional validation checks after Bootstrap validation
        if (!fileName.trim()) {
            setSubmitError({
                title: t("upload.modal.validation.title"),
                message: t("upload.modal.validation.nameRequired"),
            });
            return;
        }

        if (!selectedFile) {
            setSubmitError({
                title: t("upload.modal.validation.title"),
                message: t("upload.modal.validation.fileRequired"),
            });
            return;
        }

        if (selectedFile.type !== "application/pdf") {
            setSubmitError({
                title: t("upload.modal.validation.title"),
                message: t("upload.modal.validation.fileType"),
            });
            return;
        }

        if (maxFileSizeBytes && selectedFile.size > maxFileSizeBytes) {
            setSubmitError({
                title: t("upload.modal.validation.title"),
                message: t("upload.modal.validation.fileSize"),
            });
            return;
        }

        setIsUploading(true);

        const formData = new FormData();

        // Update the formData object
        formData.append("template_file", selectedFile, selectedFile.name);
        formData.append("name", fileName);

        try {
            const response = await apiService.uploadTemplate(formData);
            setSubmitError(null);

            if (response.status === "success") {
                logger.info("upload successful:", response);
                // Reset form
                setFileName("");
                setSelectedFile(null);
                setValidated(false);
                // Notify parent and close modal
                onSuccess();
                onHide();
                setSuccessToastMessage(t("upload.modal.upload.successAlert"));
            } else {
                logger.warn("unknown upload response:", response);
                setSubmitError({
                    title: t("upload.modal.upload.warningTitle"),
                    message: t("upload.modal.upload.warningMessage"),
                });
            }
        } catch (error: any) {
            logger.error("Upload error:", error);

            // Check for specific no-form-fields error
            if (isApiError(error) && error.errorCode === ERROR_CODE_PDF_NO_FORM_FIELDS) {
                setSubmitError({
                    title: t("upload.modal.upload.noFormFields.title"),
                    message: t("upload.modal.upload.noFormFields.message"),
                });
            } else if (isApiError(error) && error.errorCode === ERROR_CODE_PDF_CORRUPTED_FORM_FIELDS) {
                setSubmitError({
                    title: t("upload.modal.upload.corruptedFormFields.title"),
                    message: t("upload.modal.upload.corruptedFormFields.message"),
                });
            } else {
                // Generic error handling
                setSubmitError({
                    title: t("upload.modal.upload.title"),
                    message: error.message,
                });
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleModalHide = () => {
        // Reset form when modal closes
        setSubmitError(null);
        setFileName("");
        setSelectedFile(null);
        setValidated(false);
        setIsUploading(false);
        setSuccessToastMessage(null);
        onHide();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        // Check file size immediately when file is selected
        if (file && maxFileSizeBytes && file.size > maxFileSizeBytes) {
            setSubmitError({
                title: t("upload.modal.validation.title"),
                message: t("upload.modal.validation.fileSize"),
            });
            setSelectedFile(null);
            e.target.value = ""; // Clear the file input
            return;
        }

        setSelectedFile(file || null);
        setSubmitError(null); // Clear any previous errors

        // Auto-populate filename from PDF name if filename is empty
        if (file && !fileName) {
            const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
            setFileName(nameWithoutExtension);
        }
    };

    return (
        <>
            <Modal show={show} onHide={handleModalHide} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("upload.modal.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-4">{t("upload.modal.description")}</p>

                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Form.Group className="form-group mb-3" controlId="formGroupTemplateName">
                            <Form.Label>{t("upload.modal.fields.name.label")}</Form.Label>
                            <InputGroup hasValidation>
                                <Form.Control
                                    type="text"
                                    required
                                    value={fileName}
                                    onChange={(e) => setFileName(e.target.value)}
                                    placeholder={t("upload.modal.fields.name.placeholder")}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t("upload.modal.fields.name.feedback")}
                                </Form.Control.Feedback>
                            </InputGroup>
                            <Form.Text className="text-muted">{t("upload.modal.fields.name.help")}</Form.Text>
                        </Form.Group>

                        <Form.Group controlId="formFile" className="mb-4">
                            <Form.Label>{t("upload.modal.fields.file.label")}</Form.Label>
                            <InputGroup hasValidation>
                                <Form.Control
                                    type="file"
                                    accept="application/pdf"
                                    required
                                    onChange={handleFileChange}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t("upload.modal.fields.file.feedback")}
                                </Form.Control.Feedback>
                            </InputGroup>
                            <Form.Text className="text-muted">{t("upload.modal.fields.file.help")}</Form.Text>
                        </Form.Group>

                        {selectedFile && (
                            <div className="alert alert-info">
                                {t("upload.modal.selectedFile", {
                                    name: selectedFile.name,
                                    size: Math.round(selectedFile.size / 1024),
                                })}
                            </div>
                        )}

                        {submitError && (
                            <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                                <Alert.Heading>{submitError.title ?? t("upload.modal.upload.title")}</Alert.Heading>
                                <p>{submitError.message}</p>
                            </Alert>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalHide} disabled={isUploading}>
                        {t("upload.modal.buttons.cancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={isUploading}>
                        {isUploading ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                {t("upload.modal.buttons.uploading")}
                            </>
                        ) : (
                            t("upload.modal.buttons.upload")
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            <ToastSuccess
                show={successToastMessage !== null}
                message={successToastMessage || ""}
                onClose={() => setSuccessToastMessage(null)}
            />
        </>
    );
};

export default UploadModal;
